import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, processExamMarks } from "../../api/examApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Direct port of digicampusbd's `exam-result-process-mark` ("Exam Mark
 * Process") — bulk recompute every already-entered mark row for a whole
 * class+exam (all subjects) against the CURRENT `ExamMarkConfiguration`/
 * `ExamGrade` settings, without re-entering marks. Confirmed via
 * `ExamMarkService.examMarkProcess`/`processExamMark` this is the exact same
 * per-subject weighted-score/pass-fail/grade-band algorithm the already-built
 * `ExamMarkServiceImpl.fillExamMark` already implements (used by the mark-
 * entry save flow) — this new endpoint just re-runs it over every stored
 * mark row instead of freshly-submitted ones. New `POST /api/exam-marks/process`.
 * Matches the old app's own real request shape (includes `academicYear` —
 * the year the marks themselves belong to, not necessarily the institute's
 * current academic-year setting; caught via live verification that omitting
 * this parameter silently matched zero rows for real historical exam data).
 */
export default function ExamMarkProcessPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
  }, []);

  useEffect(() => {
    if (!classConfigId) return;
    const config = classConfigs.find((c) => String(c.id) === String(classConfigId));
    if (!config) return;
    listExamConfigs(config.classId).then((list) => {
      setExamConfigs(list);
      if (list.length > 0) setExamConfigId(String(list[0].examConfigId || list[0].id));
    });
  }, [classConfigId, classConfigs]);

  const handleProcess = async () => {
    setError("");
    setResult(null);
    if (!classConfigId || !examConfigId) {
      setError("Please select a section and an exam.");
      return;
    }
    setProcessing(true);
    try {
      const res = await processExamMarks(examConfigId, classConfigId, academicYear);
      setResult(res.message);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to process marks.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Exam Mark Process</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {result && <p style={{ color: "var(--color-success, green)" }}>{result}</p>}
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.map((e) => (
              <option key={e.examConfigId || e.id} value={e.examConfigId || e.id}>
                {e.examName || e.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleProcess} disabled={processing}>
          {processing ? "Processing..." : "Process"}
        </button>
      </div>
      <p>Recomputes every already-entered mark for this section's chosen exam against the current mark-configuration and grade settings.</p>
    </MainLayout>
  );
}
